// Internal: write or change one blog post (D-111). The text is Markdown; pictures are uploaded
// and put in at the cursor. Publishing, scheduling and unpublishing are buttons on the side.
import { type BlogImage, type BlogPost, slugify } from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ApiError, api, del, patch, post } from "../api";
import { AdminNav, STATUS_PILL, when } from "./AdminBlog";

interface Form {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  seoTitle: string;
  seoDescription: string;
  coverImageId: string | null;
  coverUrl: string | null;
  coverAlt: string;
  author: string;
  tags: string;
}

const EMPTY: Form = {
  title: "",
  slug: "",
  excerpt: "",
  body: "",
  seoTitle: "",
  seoDescription: "",
  coverImageId: null,
  coverUrl: null,
  coverAlt: "",
  author: "",
  tags: "",
};

const toForm = (p: BlogPost): Form => ({
  title: p.title,
  slug: p.slug,
  excerpt: p.excerpt,
  body: p.body,
  seoTitle: p.seoTitle ?? "",
  seoDescription: p.seoDescription ?? "",
  coverImageId: p.coverImageId,
  coverUrl: p.coverUrl,
  coverAlt: p.coverAlt,
  author: p.author,
  tags: p.tags.join(", "),
});

function read(file: File) {
  return new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

/** "2026-10-12T09:00" in this browser's time zone, for a datetime-local field. */
const localInput = (d: Date) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

function Counter({ value, best }: { value: string; best: [number, number] }) {
  const n = value.length;
  const ok = n === 0 || (n >= best[0] && n <= best[1]);
  return (
    <span className={`small ${ok ? "muted" : "error-text"}`} style={{ fontWeight: 400 }}>
      {n} characters{n > 0 && !ok ? `, best ${best[0]} to ${best[1]}` : ""}
    </span>
  );
}

export function AdminBlogPost() {
  const { id = "new" } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const loaded = useQuery({
    queryKey: ["blog-post", id],
    queryFn: () => api<{ post: BlogPost }>(`/v1/admin/blog/posts/${id}`),
    enabled: !isNew,
    // Never replace what's being typed with a fresh copy.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
  const [form, setForm] = useState<Form>(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState(() => localInput(new Date(Date.now() + 86_400_000)));
  const body = useRef<HTMLTextAreaElement>(null);
  const p = loaded.data?.post;

  // The form follows the saved post each time a new copy arrives, before it's drawn.
  const [shown, setShown] = useState<string | null>(null);
  if (p && shown !== `${p.id} ${p.updatedAt}`) {
    setShown(`${p.id} ${p.updatedAt}`);
    setForm(toForm(p));
    setSlugTouched(true);
    setDirty(false);
  }

  if (loaded.error)
    return (
      <div className="onb">
        {loaded.error instanceof ApiError && loaded.error.status === 404
          ? "That post wasn't found."
          : loaded.error.message}
      </div>
    );
  if (!isNew && !p) return <div className="onb muted">Loading…</div>;

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "title" && !slugTouched) next.slug = slugify(String(v));
      return next;
    });
    setDirty(true);
  };

  const payload = () => ({
    title: form.title,
    slug: form.slug,
    excerpt: form.excerpt,
    body: form.body,
    seoTitle: form.seoTitle || null,
    seoDescription: form.seoDescription || null,
    coverImageId: form.coverImageId,
    coverAlt: form.coverAlt,
    ...(form.author.trim() ? { author: form.author } : {}),
    tags: form.tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean),
  });

  const act = async (what: string, fn: () => Promise<{ summary: string; post?: BlogPost }>) => {
    setError(null);
    setMsg(null);
    setBusy(what);
    try {
      const r = await fn();
      setMsg(r.summary);
      if (r.post) {
        qc.setQueryData(["blog-post", r.post.id], { post: r.post });
        if (isNew) nav(`/admin/blog/${r.post.id}`, { replace: true });
        else {
          setForm(toForm(r.post));
          setDirty(false);
        }
      }
      qc.invalidateQueries({ queryKey: ["blog"] });
      return r;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    act("save", () =>
      isNew
        ? post<{ summary: string; post: BlogPost }>("/v1/admin/blog/posts", payload())
        : patch<{ summary: string; post: BlogPost }>(`/v1/admin/blog/posts/${id}`, payload()),
    );

  /** Saves what's typed first, so publishing never puts an older text live. */
  const saveThen = async (
    what: string,
    fn: (postId: string) => Promise<{ summary: string; post?: BlogPost }>,
  ) => {
    let postId = p?.id;
    if (isNew || dirty) {
      const saved = await save();
      if (!saved?.post) return;
      postId = saved.post.id;
    }
    await act(what, () => fn(postId!));
  };

  const upload = async (file: File | undefined, alt: string) => {
    if (!file) return null;
    setError(null);
    setBusy("image");
    try {
      return (
        await post<{ summary: string; image: BlogImage }>("/v1/admin/blog/images", {
          data: await read(file),
          alt,
        })
      ).image;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The upload didn't work.");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const insertPicture = async (file: File | undefined) => {
    const alt = file?.name.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " ") ?? "";
    const img = await upload(file, alt);
    if (!img) return;
    const el = body.current;
    const pos = el ? el.selectionStart : Number.MAX_SAFE_INTEGER;
    const md = `\n\n![${alt}](${img.url})\n\n`;
    // Into the text as it is now: typing may have gone on during the upload.
    setForm((f) => ({ ...f, body: f.body.slice(0, pos) + md + f.body.slice(pos) }));
    setDirty(true);
  };

  const status = p?.status ?? "draft";
  const site = p ? new URL(p.url).origin : "https://brandwall.app";
  return (
    <div className="content" style={{ margin: "0 auto" }}>
      <div className="stack" style={{ gap: 18 }}>
        <AdminNav here="blog" />
        <div className="row wrap">
          <h1 className="spacer">{isNew ? "New post" : form.title || "Untitled"}</h1>
          <span className={`pill ${STATUS_PILL[status].cls}`}>{STATUS_PILL[status].label}</span>
        </div>
        {error && <div className="error">{error}</div>}
        {msg && (
          <div className="notice" style={{ wordBreak: "break-word" }}>
            {msg}
          </div>
        )}
        <div className="blog-editor">
          <section className="card stack">
            <label className="field">
              Title
              <input
                className="input"
                value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="How to get permission for customer photos"
              />
            </label>
            <label className="field">
              Address
              <span className="row" style={{ gap: 0 }}>
                <span className="small muted" style={{ whiteSpace: "nowrap", paddingRight: 6 }}>
                  {site.replace(/^https?:\/\//, "")}/blog/
                </span>
                <input
                  className="input"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set("slug", e.target.value);
                  }}
                  onBlur={() => set("slug", slugify(form.slug))}
                  placeholder="permission-for-customer-photos"
                />
              </span>
              {p && p.oldSlugs.length > 0 && (
                <span className="small muted" style={{ fontWeight: 400 }}>
                  Old addresses that forward here: {p.oldSlugs.map((s) => `/blog/${s}`).join(", ")}
                </span>
              )}
              {p && form.slug !== p.slug && (
                <span className="small muted" style={{ fontWeight: 400 }}>
                  When you save, /blog/{p.slug} will forward to the new address.
                </span>
              )}
            </label>
            <label className="field">
              Excerpt
              <textarea
                className="input"
                rows={2}
                value={form.excerpt}
                onChange={(e) => set("excerpt", e.target.value)}
                placeholder="One or two sentences for the blog's front page and the feed."
              />
            </label>
            <label className="field">
              <span className="row">
                <span className="spacer">Text (Markdown)</span>
                <span className="small muted" style={{ fontWeight: 400 }}>
                  ## heading · **bold** · [link](/pricing) · - list · &gt; quote
                </span>
              </span>
              <textarea
                ref={body}
                className="input mono"
                rows={24}
                value={form.body}
                onChange={(e) => set("body", e.target.value)}
              />
            </label>
            <label className="btn secondary sm" style={{ justifySelf: "start" }}>
              {busy === "image" ? "Uploading…" : "Add a picture at the cursor"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                hidden
                onChange={(e) => {
                  insertPicture(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
          </section>

          <aside className="stack">
            <section className="card stack">
              <button
                type="button"
                className="btn"
                disabled={!form.title.trim() || busy !== null || (!isNew && !dirty)}
                onClick={save}
              >
                {busy === "save" ? "Saving…" : isNew ? "Save draft" : dirty ? "Save" : "Saved"}
              </button>
              {p?.status === "published" && (
                <p className="small">
                  Live since {when(p.publishedAt!)} ·{" "}
                  <a href={p.url} target="_blank" rel="noreferrer">
                    View
                  </a>
                </p>
              )}
              {p?.status === "scheduled" && (
                <p className="small">Goes live {when(p.publishedAt!)}</p>
              )}
              {p && (
                <a
                  className="btn secondary sm"
                  href={p.previewUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Preview
                </a>
              )}
              {status !== "published" && (
                <button
                  type="button"
                  className="btn secondary sm"
                  disabled={!form.title.trim() || busy !== null}
                  onClick={() =>
                    saveThen("publish", (pid) => post(`/v1/admin/blog/posts/${pid}/publish`, {}))
                  }
                >
                  Publish now
                </button>
              )}
              <div className="stack" style={{ gap: 6 }}>
                <span className="small" style={{ fontWeight: 600 }}>
                  {status === "scheduled" ? "Move the time" : "Schedule"}
                </span>
                <div className="row">
                  <input
                    type="datetime-local"
                    className="input"
                    value={at}
                    min={localInput(new Date())}
                    onChange={(e) => setAt(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn secondary sm"
                    disabled={!form.title.trim() || !at || busy !== null}
                    onClick={() => {
                      const d = new Date(at);
                      if (!(d.getTime() > Date.now())) {
                        setError("Choose a time in the future.");
                        return;
                      }
                      saveThen("schedule", (pid) =>
                        post(`/v1/admin/blog/posts/${pid}/publish`, { at: d.toISOString() }),
                      );
                    }}
                  >
                    Schedule
                  </button>
                </div>
              </div>
              {status !== "draft" && (
                <button
                  type="button"
                  className="btn secondary sm"
                  disabled={busy !== null}
                  onClick={() =>
                    act("unpublish", () => post(`/v1/admin/blog/posts/${p!.id}/unpublish`))
                  }
                >
                  {status === "scheduled" ? "Cancel the schedule" : "Unpublish"}
                </button>
              )}
            </section>

            {p && p.warnings.length > 0 && (
              <section className="card stack" style={{ gap: 8 }}>
                <b>To look at</b>
                <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
                  {p.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </section>
            )}

            <section className="card stack">
              <b>Cover picture</b>
              {form.coverUrl && (
                <img
                  src={form.coverUrl}
                  alt={form.coverAlt}
                  style={{
                    width: "100%",
                    borderRadius: 8,
                    aspectRatio: "16 / 9",
                    objectFit: "cover",
                  }}
                />
              )}
              <div className="row wrap">
                <label className="btn secondary sm">
                  {busy === "image" ? "Uploading…" : form.coverUrl ? "Change" : "Upload"}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    hidden
                    onChange={async (e) => {
                      const img = await upload(e.target.files?.[0], form.coverAlt);
                      e.target.value = "";
                      if (img) {
                        setForm((f) => ({ ...f, coverImageId: img.id, coverUrl: img.url }));
                        setDirty(true);
                      }
                    }}
                  />
                </label>
                {form.coverUrl && (
                  <button
                    type="button"
                    className="btn link small"
                    onClick={() => {
                      setForm((f) => ({ ...f, coverImageId: null, coverUrl: null }));
                      setDirty(true);
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
              <label className="field">
                What it shows
                <input
                  className="input"
                  value={form.coverAlt}
                  onChange={(e) => set("coverAlt", e.target.value)}
                />
              </label>
            </section>

            <section className="card stack">
              <b>Search and sharing</b>
              <label className="field">
                <span className="row">
                  <span className="spacer">Search title</span>
                  <Counter value={form.seoTitle || form.title} best={[20, 60]} />
                </span>
                <input
                  className="input"
                  value={form.seoTitle}
                  onChange={(e) => set("seoTitle", e.target.value)}
                  placeholder="The title, when left empty"
                />
              </label>
              <label className="field">
                <span className="row">
                  <span className="spacer">Search description</span>
                  <Counter value={form.seoDescription || form.excerpt} best={[50, 160]} />
                </span>
                <textarea
                  className="input"
                  rows={3}
                  value={form.seoDescription}
                  onChange={(e) => set("seoDescription", e.target.value)}
                  placeholder="The excerpt, when left empty"
                />
              </label>
              <label className="field">
                Tags, with commas
                <input
                  className="input"
                  value={form.tags}
                  onChange={(e) => set("tags", e.target.value)}
                  placeholder="UGC, Shopify"
                />
              </label>
              <label className="field">
                Author
                <input
                  className="input"
                  value={form.author}
                  onChange={(e) => set("author", e.target.value)}
                  placeholder="Your first name, when left empty"
                />
              </label>
            </section>

            {p && (
              <button
                type="button"
                className="btn link small"
                style={{ justifySelf: "start" }}
                disabled={busy !== null}
                onClick={async () => {
                  if (!window.confirm(`Delete "${p.title}" for good?`)) return;
                  try {
                    await del(`/v1/admin/blog/posts/${p.id}`);
                    qc.invalidateQueries({ queryKey: ["blog"] });
                    nav("/admin/blog");
                  } catch (e) {
                    setError(e instanceof ApiError ? e.message : "That didn't work.");
                  }
                }}
              >
                Delete this post
              </button>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
